<template>
  <div
    v-if="isLoaded"
    class="smart-callout-container"
    :class="jyranianClass"
    data-test="smart-callout"
  >
    <div class="smart-callout-image-container">
      <img
        v-if="jyranian === 'Apollo'"
        src="../../assets/jyranians/zen-jyranian.svg"
        alt="a wise zen jyranian called Apollo"
        class="smart-jyranian"
        data-test="smart-callout-image"
      />
    </div>
    <div class="smart-callout-box">
      <!-- Show regeneration button when manual triggering is required -->
      <div v-if="isManuallyTriggered && !isStreaming && !chatIsLoading">
        <button
          type="button"
          @click="() => askPrompt(true)"
          class="btn btn-sm btn-default-reload center-flex"
          style="z-index: 999999"
        >
          Regenerate
        </button>
      </div>

      <!-- Show the AI response if the stream is being received -->
      <div
        v-if="!chatIsLoading"
        v-markdown="text"
        class="smart-text"
        data-test="smart-callout-text"
      />

      <!-- Show user generation button -->
      <div
        v-else-if="isManuallyTriggered && !isStreaming"
        class="center-flex"
        style="flex-direction: column"
      >
        <button
          type="button"
          @click="() => askPrompt(false)"
          class="btn btn-sm btn-default-reload"
          style="z-index: 999999"
        >
          Generate
        </button>
        <p style="color: rgb(85, 85, 85); font-size: 12px; text-align: center; margin-top: 10px">
          If you've just edited a text box: please wait a moment before pressing this button. Gyre
          needs a few seconds to process your input. If nothing happens after pressing the button,
          please try it again.
        </p>
      </div>

      <!-- Show loading if the callout doesn't require manual triggering -->
      <LoadingSpinner
        v-else-if="chatIsLoading"
        :type="'Dots'"
        size="200px"
        data-test="smart-callout-loading"
      />

      <div v-if="jyranian !== 'Apollo'" class="smart-callout-image-container">
        <img
          v-if="jyranian === 'Emile'"
          src="../../assets/jyranians/jyranian-clipboard-2.svg"
          alt="an older Jyranian with a clipboard called Emile"
          class="smart-jyranian"
          data-test="smart-callout-image"
        />
        <img
          v-else-if="jyranian === 'Klara'"
          src="../../assets/jyranians/jyranian-female-side-2.svg"
          alt="an energetic female Jyranian called Klara"
          class="smart-jyranian"
          data-test="smart-callout-image"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
/* eslint-disable */
import { computed, onMounted, ref, watch } from "vue";
import cms from "../../services/cms/cms";
import chat from "../../components/smart-scratchpad/chat";
import LoadingSpinner from "../loading-spinner.vue";
import { useCmsSessionStore } from "../../stores/useCmsSessionStore";
import pinia from "../../stores";
import processPromptVars, {
  getDataContext,
} from "../../components/smart-scratchpad/process-prompt-vars";
import { renderErrorMessageToClient } from "../smart-scratchpad/variable-error-helpers";
import { SmartJyranian } from "../../services/cms/cms-type-definitions";
import { Analytics } from "../../services/analytics/analytics";
import { csContext } from "../../services/cs-context";
import { AxiosError } from "axios";

import smartTextBoxStore from "../../stores/smart-text-box-store";

let cmsSessionStore = useCmsSessionStore(pinia());

const props = defineProps<{ uid?: string }>();
const text = ref<string>("");
const chatIsLoading = computed(() => text.value.length === 0);
const isManuallyTriggered = ref<boolean | null>(null);
const isLoaded = ref(false);

const prompt = ref<string>("");
const jyranian = ref<SmartJyranian>("Apollo");
const isStreaming = ref(false);

const hasRequiredData = computed(
  () =>
    cmsSessionStore.hasFinishedMapping.strengths ||
    (csContext.userDocument.hasCompletedFoundationsSession &&
      !window.location.href.includes("/foundations/remapping")),
);

const textBoxVersions = computed(() => smartTextBoxStore.$state.version);
const jyranianClass = computed(() => jyranian.value.toLowerCase());

let version = 0;
let dataContext: any = null;

watch(textBoxVersions, async () => {
  version++;

  if (!isManuallyTriggered.value) {
    await askPrompt();
  }
});

watch(hasRequiredData, async () => {
  if (hasRequiredData.value) {
    dataContext = getDataContext();

    if (!isManuallyTriggered.value) {
      await askPrompt();
    }
  }
});

async function askPrompt(bypassCache = false): Promise<void> {
  let activeVersion = version;
  isStreaming.value = true;
  text.value = "";

  if (prompt.value?.trim().length > 0 && hasRequiredData.value) {
    const { processed, errors } = await processPromptVars(prompt.value, dataContext);

    if (errors.length > 0) {
      text.value = renderErrorMessageToClient(errors);
      return;
    }

    Analytics.action("Ran Smart Callout", "SmartCalloutActions", {
      prompt: prompt.value,
      processed,
    });

    chat(
      processed,
      (content) => {
        if (activeVersion === version) {
          text.value = content.trim();
        }
      },
      bypassCache,
    )
      .then((message) => {
        Analytics.action("Completed Smart Callout", "SmartCalloutActions", {
          prompt: prompt.value,
          response: message.trim(),
        });
      })
      .catch((e) => {
        text.value = "Sorry, there was an error. Please refresh the page.";
        const error = e as AxiosError;
        const errorMessage = `${error.response?.status}: ${error.response?.statusText}`;
        Analytics.action("Failed Smart Callout", "SmartCalloutActions", {
          prompt: prompt.value,
          error: errorMessage,
        });
      })
      .finally(() => {
        isStreaming.value = false;
      });
  }
}

onMounted(async () => {
  const { data } = await cms.getByUid(
    "cms-smart-callout",
    props.uid ?? "smart-callout-solo-summary",
  );

  isManuallyTriggered.value = data.manually_triggered ?? false;
  prompt.value = data.prompt ?? data.example;
  jyranian.value = data.jyranian ?? "Apollo";
  dataContext = getDataContext();
  if (!isManuallyTriggered.value) {
    await askPrompt();
  }

  isLoaded.value = true;
});
</script>

<style scoped lang="less">
@import (reference) "../../styles/bootstrap/variables.less";

.smart-callout-container {
  width: 100%;
  display: flex;
  flex-direction: column;
}

.smart-callout-box {
  padding: 2rem;
  border: 1px solid @btn-default-border;
  border-radius: 2rem;
  background-color: #ffffff;

  @media (max-width: @screen-sm-max) {
    width: auto;
    margin-left: 0;
  }
}

.smart-callout-image-container {
  width: 100%;
  z-index: 50;
  display: flex;
  justify-content: center;

  @media (max-width: @screen-sm-max) {
    float: unset;
    margin: 0 auto;
    width: 50%;
  }
}

.apollo .smart-callout-image-container {
  margin-bottom: -60px;

  @media (max-width: @screen-xxs-max) {
    margin-bottom: -40px;
  }
}

.emile,
.klara {
  .smart-callout-box {
    padding-bottom: 0;
  }

  .smart-jyranian {
    position: relative;
  }
}

.emile .smart-jyranian {
  left: 26%;

  @media (max-width: @screen-sm-max) {
    left: 50%;
  }
}

.klara .smart-jyranian {
  left: 30%;

  @media (max-width: @screen-sm-max) {
    left: 60%;
    max-height: 200px;
    width: auto;
  }
}

.smart-jyranian {
  width: 40%;

  @media (max-width: @screen-sm-max) {
    width: 80%;
  }
}

.smart-text {
  padding-top: 20px;
  white-space: pre-wrap;
  user-select: text;
  position: relative;
  z-index: 999999;
}
</style>
